import { NavmeshArea, NavmeshEmissionZone, NavmeshLevel } from '../api/Navmesh';
import { NavmeshBuilderNavmeshResponse } from '../map/EditTools/useBuildNavmesh';
import {
  NavGraph,
  NavGraphLink,
  NavGraphLinkData,
  NavGraphNode,
  NavGraphNodeData,
  NavGraphUnit,
} from './NavGraph';
import { pointBetween } from './navGraphGeometryUtils';

type EditTrackingKey = 'unit' | `${'node' | 'link' | 'level' | 'area' | 'emissionZone'}-${string}`;
export type TimeService = { now: () => Date };

const defaultTime: TimeService = { now: () => new Date() };

/**
 * NavGraph with mutable edits and a source graph. Tracks changes for revert/save; fires onChange when state changes.
 * Use setSource to load the graph to edit; edits are applied in-place, with revert* methods restoring from source.
 */
export class EditModeNavGraph extends NavGraph {
  private time: TimeService = defaultTime;

  private source: NavGraph | undefined;
  private edits: Map<EditTrackingKey, true> = new Map();
  /** Number of tracked edits (nodes, links, levels, areas, emissionZones, unit). */
  get editCount() {
    return this.edits.size;
  }

  public get unit(): NavGraphUnit {
    return this._unit;
  }
  set unit(newUnit: NavGraphUnit) {
    this._unit = newUnit;
    const originalUnit = this.source?.unit;
    if (originalUnit === newUnit) {
      this.edits.delete('unit');
    } else {
      this.edits.set('unit', true);
    }
  }

  /** Called after any edit. Optional; may be used to trigger React state updates. */
  public onChange: undefined | (() => void);

  /** Optional handlers: onChange (invoked after edits), time (for updatedAt in toJSON). */
  constructor(handlers?: { onChange?: () => void; time?: TimeService }) {
    super();
    this.onChange = handlers?.onChange;
    if (handlers?.time !== undefined) {
      this.time = handlers.time;
    }
  }

  /** True if there is no source or the source has no links (e.g. new project). */
  isNew() {
    return this.source ? this.source.linkCount === 0 : true;
  }

  /** Serialises the graph to Navmesh shape. Options: linkIds to export only those links (and their nodes).
   * If there are edits, sets updatedAt to current time.
   */
  toJSON(options?: { linkIds?: number[] }) {
    const json = super.toJSON(options);
    if (this.editCount > 0) {
      json.updatedAt = this.time.now().toISOString();
    }
    return json;
  }

  /**
   * Sets the source graph to edit. Replaces current state with a copy of source (or empty if undefined). Clears edit tracking.
   * @param navgraph Source graph; undefined = empty editable graph.
   */
  setSource(navgraph?: NavGraph) {
    this.source = navgraph;
    this.reset();
  }

  /**
   * Removes all data and all edits, but leaves the source intact.
   */
  empty() {
    this.clear();
    this.edits.clear();
    this.onChange?.();
  }

  /**
   * Overwites the state with the passed in navmesh, leaving the source intact.
   * Optional assignNewLevel assigns new links to that level
   * @param navmesh Navmesh to overwrite the state with.
   * @param assignNewLevel Optional level to assign new links to.
   */
  overwriteWith(
    { nodes, links, levels, areas, emissionZones, unit }: NavmeshBuilderNavmeshResponse,
    assignNewLevel?: { id: number; name: string },
  ) {
    this.clear();
    this.edits.clear();

    levels?.forEach(({ id, ...data }) => this.levels.set(id, data));
    if (assignNewLevel !== undefined && !this.levels.has(assignNewLevel.id)) {
      this.levels.set(assignNewLevel.id, { name: assignNewLevel.name.trim() });
    }

    areas?.forEach(({ id, ...data }) => this.areas.set(id, data));
    emissionZones?.forEach(({ id, ...data }) => this.emissionZones.set(id, data));
    nodes?.forEach(({ id, pos }) => this.addNode(id, { pos }));
    links.forEach(({ a, b, ...data }) => {
      if (data.new) {
        data.level = assignNewLevel?.id;
      }
      this.addLink(a, b, data);
    });
    if (unit === 'm' || unit === 'ft') {
      this.unit = unit;
    }

    this.onChange?.();
  }

  /** Discards all edits and repopulates from source (or leaves empty if no source). */
  reset() {
    this.clear();
    this.edits.clear();
    if (!this.source) return;

    this.source.forEachNode(({ id, data }) => {
      if (typeof id !== 'number') {
        console.warn('Found node with non-numeric id', id, data);
        return;
      }
      super.addNode(id, structuredClone(data));
    });
    this.source.forEachLink(({ fromId, toId, data }) => {
      if (typeof fromId !== 'number' || typeof toId !== 'number') {
        console.warn('Found link with non-numeric from/to ids', fromId, toId, data);
        return;
      }
      super.addLink(fromId, toId, structuredClone(data));
    });
    this.areas = structuredClone(this.source.areas);
    this.levels = structuredClone(this.source.levels);
    this.emissionZones = structuredClone(this.source.emissionZones);
    this.unit = this.source.unit;
    this.clientId = this.source.clientId;
    this.projectId = this.source.projectId;
    this.createdAt = this.source.createdAt;
    this.updatedAt = this.source.updatedAt;

    this.onChange?.();
  }

  /** Adds a link and marks it as edited. */
  addLink(fromId: number, toId: number, data: NavGraphLinkData) {
    super.addLink(fromId, toId, data);
    this.edits.set(`link-${data.id}`, true);
    this.onChange?.();
  }
  /** Updates link data in-place and marks as edited. */
  editLinkData(link: NavGraphLink, updates: Partial<NavGraphLinkData>) {
    link.data = { ...link.data, ...updates };
    this.edits.set(`link-${link.data.id}`, true);
    this.onChange?.();
  }
  /** Removes link and any orphaned nodes; marks as edit if link existed in source. */
  deleteLink(link: NavGraphLink) {
    this.graph.removeLink(link);
    this.linkIds.delete(link.data.id);
    const existingLink = this.source?.getLink(link.data.id) !== undefined;
    if (existingLink) {
      this.edits.set(`link-${link.data.id}`, true);
    } else {
      this.edits.delete(`link-${link.data.id}`);
    }
    // Remove any nodes now not connected to a link
    [link.fromId, link.toId].forEach(nodeId => {
      const node = this.graph.getNode(nodeId);
      if (!node || !!node.links) return;

      this.graph.removeNode(nodeId);
      const existingNode = this.source?.getNode(nodeId);
      if (existingNode) {
        this.edits.set(`node-${nodeId}`, true);
      } else {
        this.edits.delete(`node-${nodeId}`);
      }
    });
    this.onChange?.();
  }
  /** Restores link from source or deletes it if it was new. */
  revertLink(link: NavGraphLink) {
    const original = this.source?.getLink(link.data.id);
    if (!original) {
      this.deleteLink(link);
      return;
    }
    if (original.fromId === link.fromId && original.toId === link.toId) {
      link.data = structuredClone(original.data);
      this.edits.delete(`link-${link.data.id}`);
      this.onChange?.();
    } else {
      this.deleteLink(link);
    }
  }
  /**
   * Inserts a node at fraction portion along the link and replaces link with two; returns the new link from fromNode to new node.
   * @param link Link to split.
   * @param portion Fraction along the link to insert the new node at, between 0 and 1.
   * @returns The new link from fromNode to new node.
   */
  splitLink(link: NavGraphLink, portion = 0.5): NavGraphLink | undefined {
    const fromNode = this.graph.getNode(link.fromId);
    const toNode = this.graph.getNode(link.toId);
    if (!fromNode || !toNode) return;

    const newLinkData = { ...structuredClone(link.data), id: this.nextLinkId() };

    const newNodeId = this.nextNodeId();
    const newPoint = pointBetween(fromNode.data.pos, toNode.data.pos, portion);
    this.addNode(newNodeId, { pos: newPoint });
    this.graph.removeLink(link);
    this.linkIds.delete(link.data.id);
    const existingLink = this.source?.getLink(link.data.id) !== undefined;
    if (existingLink) {
      this.edits.set(`link-${link.data.id}`, true);
    } else {
      this.edits.delete(`link-${link.data.id}`);
    }
    this.addLink(Number(fromNode.id), newNodeId, link.data);
    this.addLink(newNodeId, Number(toNode.id), newLinkData);

    this.onChange?.();

    return this.graph.getLink(fromNode.id, newNodeId);
  }

  /** Updates node position in-place and marks as edited. */
  editNode(node: NavGraphNode, update: NavGraphNodeData) {
    node.data.pos.splice(0, 3, ...update.pos);
    this.edits.set(`node-${node.id}`, true);
    this.onChange?.();
  }
  /** Removes node and its links; optional bridge adds a single link between the two adjacent nodes. */
  deleteNode(
    node: NavGraphNode,
    bridge?: { fromNodeId: number; toNodeId: number; data: Omit<NavGraphLinkData, 'id'> },
  ) {
    const links: NavGraphLink[] = Array.from(node.links?.values() ?? []);
    this.graph.removeNode(node.id);
    this.nodeIds.delete(Number(node.id));
    const existingNode = this.source?.getNode(node.id);
    if (existingNode) {
      this.edits.set(`node-${node.id}`, true);
    } else {
      this.edits.delete(`node-${node.id}`);
    }

    if (bridge) {
      this.addLink(bridge.fromNodeId, bridge.toNodeId, { id: this.nextLinkId(), ...bridge.data });
    }

    links.forEach(link => {
      this.deleteLink(link);
    });
    this.onChange?.();
  }
  /** Restores node from source or deletes it if it was new. */
  revertNode(node: NavGraphNode) {
    const original = this.source?.getNode(node.id);
    if (!original) {
      this.deleteNode(node);
      this.onChange?.();
      return;
    }

    node.data = structuredClone(original.data);
    this.edits.delete(`node-${node.id}`);
    this.onChange?.();
  }

  /** Updates area metadata and marks as edited. */
  editArea(id: number, area: Omit<NavmeshArea, 'id'>) {
    this.areas.set(id, area);
    this.edits.set(`area-${id}`, true);
    this.onChange?.();
  }
  /** Removes area; links may be reassigned to another area via options.reassignTo. */
  deleteArea(id: number, options?: { reassignTo?: number }) {
    const reassignToArea = options?.reassignTo;
    this.graph.forEachLink(link => {
      if (link.data.area === id) {
        link.data.area = reassignToArea;
        this.edits.set(`link-${link.data.id}`, true);
      }
    });
    this.areas.delete(id);
    const existingArea = this.source?.areas.has(id);
    if (existingArea) {
      this.edits.set(`area-${id}`, true);
    } else {
      this.edits.delete(`area-${id}`);
    }
    this.onChange?.();
  }
  /** Restores area from source. */
  revertArea(id: number) {
    const original = this.source?.areas.get(id);
    if (original) {
      this.areas.set(id, structuredClone(original));
      this.edits.delete(`area-${id}`);
    }
    this.onChange?.();
  }

  /** Updates emission zone metadata and marks as edited. */
  editEmissionZone(id: number, emissionZone: Omit<NavmeshEmissionZone, 'id'>) {
    this.emissionZones.set(id, emissionZone);
    this.edits.set(`emissionZone-${id}`, true);
    this.onChange?.();
  }
  /** Removes emission zone; links may be reassigned via options.reassignTo. */
  deleteEmissionZone(id: number, options?: { reassignTo?: number }) {
    const reassignToEmissionZone = options?.reassignTo;
    this.graph.forEachLink(link => {
      if (link.data.emissionZone === id) {
        link.data.emissionZone = reassignToEmissionZone;
        this.edits.set(`link-${link.data.id}`, true);
      }
    });
    this.emissionZones.delete(id);
    const existingEmissionZone = this.source?.emissionZones.has(id);
    if (existingEmissionZone) {
      this.edits.set(`emissionZone-${id}`, true);
    } else {
      this.edits.delete(`emissionZone-${id}`);
    }
    this.onChange?.();
  }
  /** Restores emission zone from source, or deletes if it was new. */
  revertEmissionZone(id: number) {
    const original = this.source?.emissionZones.get(id);
    if (original) {
      this.emissionZones.set(id, structuredClone(original));
      this.edits.delete(`emissionZone-${id}`);
    } else {
      this.deleteEmissionZone(id);
    }
    this.onChange?.();
  }

  /** Updates level metadata and marks as edited. */
  editLevel(id: number, level: Omit<NavmeshLevel, 'id'>) {
    this.levels.set(id, level);
    this.edits.set(`level-${id}`, true);
    this.onChange?.();
  }
  /** Removes level; links may be reassigned via options.reassignTo. */
  deleteLevel(id: number, options?: { reassignTo?: number }) {
    const reassignToLevel = options?.reassignTo;
    this.graph.forEachLink(link => {
      if (link.data.level === id) {
        link.data.level = reassignToLevel;
        this.edits.set(`link-${link.data.id}`, true);
      }
    });
    this.levels.delete(id);
    const existingLevel = this.source?.levels.has(id);
    if (existingLevel) {
      this.edits.set(`level-${id}`, true);
    } else {
      this.edits.delete(`level-${id}`);
    }
    this.onChange?.();
  }
  /** Restores level from source, or deletes if it was new. */
  revertLevel(id: number) {
    const original = this.source?.levels.get(id);
    if (original) {
      this.levels.set(id, structuredClone(original));
      this.edits.delete(`level-${id}`);
    } else {
      this.deleteLevel(id);
    }
    this.onChange?.();
  }
}
